import { Suspense, useCallback, useRef } from 'react';

import { Skeleton } from '@components/ui/skeleton';
import { type Note } from '@models/Note';
import { type IUpdateFolderInputData } from '@services/folders/dtos/updateFolder/InputData';
import FolderService from '@services/folders/FolderService';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import List from './List';
import MoveDialog, {
  type IOpenMoveFolderInputData,
  type IRefProps as MoveDialogRef,
} from './MoveDialog';
import RenameDialog, {
  type IRefProps as RenameDialogRef,
} from './RenameDialog';

function NotesRoot() {
  const queryClient = useQueryClient();

  const renameDialogRef = useRef<RenameDialogRef>(null);
  const moveDialogRef = useRef<MoveDialogRef>(null);

  const {
    isPending: isPendingUpdateFolder,
    mutateAsync: mutateAsyncUpdateFolder,
    variables: variablesUpdateFolder,
  } = useMutation<void, unknown, IUpdateFolderInputData>({
    mutationFn: FolderService.update,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notes'] }),
  });

  const handleUpdateFolder = useCallback(
    async (data: IUpdateFolderInputData) => {
      await mutateAsyncUpdateFolder(data);
    },
    [mutateAsyncUpdateFolder],
  );

  const handleOpenRenameDialog = useCallback((note: Note) => {
    renameDialogRef.current?.open(note);
  }, []);

  const handleOpenMoveDialog = useCallback(
    (inputData: IOpenMoveFolderInputData) => {
      moveDialogRef.current?.open(inputData);
    },
    [],
  );

  return (
    <div>
      <div>
        <Suspense
          fallback={
            <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
              {new Array(20).fill(0).map((_, index) => (
                <Skeleton className={`h-16 w-full`} key={index} />
              ))}
            </div>
          }>
          <List
            idUpdateFolder={variablesUpdateFolder?.id}
            isPendingUpdateFolder={isPendingUpdateFolder}
            onOpenMoveDialog={handleOpenMoveDialog}
            onOpenRenameDialog={handleOpenRenameDialog}
            onUpdateFolder={handleUpdateFolder}
          />
        </Suspense>
      </div>

      <RenameDialog onUpdateFolder={handleUpdateFolder} ref={renameDialogRef} />

      <MoveDialog ref={moveDialogRef} />
    </div>
  );
}

export default NotesRoot;
